import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useRacing } from "../context/RacingContext";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { VEHICLE_TYPES } from "../constants/options";
import { LogOut, Save, Plus, X } from "lucide-react";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const { runs } = useRacing();
  const [name, setName] = useState(user?.name || "");
  const [vehicleType, setVehicleType] = useState(user?.vehicleType || "car");
  const [vehicleDescription, setVehicleDescription] = useState(user?.vehicleDescription || "");
  const [tracks, setTracks] = useState(user?.tracks || []);
  const [newTrack, setNewTrack] = useState("");
  const [saving, setSaving] = useState(false);

  const inputCls = "w-full rounded-lg border border-mt-border bg-mt-card px-3 py-2.5 text-sm outline-none focus:border-mt-primary focus:ring-1 focus:ring-mt-primary";

  const addTrack = () => {
    if (!newTrack.trim()) return;
    setTracks((t) => [...new Set([...t, newTrack.trim()])]);
    setNewTrack("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { name, vehicleType, vehicleDescription, tracks });
      setUser((u) => ({ ...u, name, vehicleType, vehicleDescription, tracks }));
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="font-head text-3xl font-black tracking-tighter">Profile</h1>

      <div className="flex items-center gap-4 rounded-xl border border-mt-border bg-mt-card p-5 shadow-sm">
        <div className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-mt-primary font-head text-2xl font-black text-white">
          {(user?.name || user?.email || "R")[0].toUpperCase()}
        </div>
        <div>
          <div className="font-head text-lg font-bold">{user?.name || "Racer"}</div>
          <div className="text-sm text-mt-mutedfg">{user?.email}</div>
          <div className="mt-1 text-xs font-semibold text-mt-primary">{runs.length} runs logged</div>
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-mt-border bg-mt-card p-5 shadow-sm">
        <Field label="Racer Name">
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
        </Field>
        <Field label="Vehicle Type">
          <div className="flex flex-wrap gap-1.5">
            {VEHICLE_TYPES.map((v) => (
              <button key={v.value} onClick={() => setVehicleType(v.value)} data-testid={`profile-vehicle-${v.value}`}
                className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${vehicleType === v.value ? "border-mt-primary bg-mt-primary text-white" : "border-mt-border bg-mt-card hover:border-mt-primary"}`}>
                {v.label}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Vehicle Description">
          <input className={inputCls} value={vehicleDescription} onChange={(e) => setVehicleDescription(e.target.value)} placeholder="1969 Camaro, 632 BBC" data-testid="profile-vehicle-desc" />
        </Field>
        <Field label="My Tracks">
          <div className="flex gap-2">
            <input className={inputCls} value={newTrack} onChange={(e) => setNewTrack(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTrack())} placeholder="Add a track" data-testid="profile-new-track" />
            <Button type="button" onClick={addTrack} data-testid="profile-add-track" className="shrink-0 bg-mt-text text-white"><Plus size={16} /></Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {tracks.map((t) => (
              <span key={t} className="flex items-center gap-1 rounded-full bg-mt-secondary px-3 py-1 text-xs font-semibold">
                {t}
                <button onClick={() => setTracks((arr) => arr.filter((x) => x !== t))} className="text-mt-mutedfg hover:text-mt-text"><X size={12} /></button>
              </span>
            ))}
          </div>
        </Field>

        <Button onClick={save} disabled={saving} data-testid="profile-save" className="w-full gap-2 bg-mt-primary text-white hover:bg-mt-primary/90">
          <Save size={16} /> Save Profile
        </Button>
      </div>

      <Button onClick={() => { logout(); window.location.replace(window.location.origin); }} data-testid="logout-button" variant="outline" className="w-full gap-2 border-mt-border">
        <LogOut size={16} /> Log Out
      </Button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-mt-mutedfg">{label}</label>
      {children}
    </div>
  );
}
